import {
    FieldHelperText,
    LabelWithIconAndTooltip,
    getIconForProperty,
    getReferenceFrom,
    useEntityFetch,
    useNavigationController,
    useReferenceDialog,
    useTranslation,
    type Entity,
    type EntityCollection,
    type User,
    type EntityReference,
    type FieldProps,
    type ResolvedProperty,
} from "@firecms/core";
import {
    Button,
    Chip,
    CloseIcon,
    EditIcon,
    Typography,
    cls,
    fieldBackgroundMixin,
} from "@firecms/ui";
import { type Tag } from "@one-ai/shared/cms/schema";

import type { CmsLocale } from "../i18n/locales";
import { tagDisplayName } from "../lib/tagLabel";

export interface TagPickerStrings {
    /** The button that opens the tag picker. */
    edit: string;
    /** Shown in place of the chips when the article has no tags. */
    empty: string;
    /** Tooltip and accessible name of a chip's remove button. */
    remove: (name: string) => string;
}

/**
 * An article's tags, as a row of chips with one picker button.
 *
 * **Replaces `ArrayOfReferencesFieldBinding`, which is the wrong shape here for
 * two reasons.**
 *
 * It renders every reference as an `EntityPreview` card — a 40px collection
 * icon, the document id in mono, the label, and the drag handle and delete
 * button of the surrounding `ArrayContainer` — stacked with `gap-4`. That is
 * about 60px of form per tag, and an article now carries several. A tag is one
 * short word in two languages; a chip is the whole of what there is to show.
 *
 * It also puts an entity link on each card (the `KeyboardTabIcon` button), which
 * opens the *tag itself* for editing in a side panel. Assigning a tag and
 * editing one are different jobs with different consequences — a label edit
 * rewrites that chip on every article carrying it, and retiring a tag removes
 * its page — so the way to it is the Tags collection, not a field on an
 * article. Nothing here opens a tag for editing.
 *
 * Everything that decides *which* tags may be assigned still lives on the
 * property: `of.path` and `of.forceFilter` are read straight off it, so the
 * retired-tag filter is declared in `collections/news.ts` exactly once and
 * `lib/searchFilter.ts` keeps applying it under a search string. The save-time
 * half of that rule is `assertNoRetiredTags`, and it is unaffected by this
 * field.
 */
export function TagChipsField({
    propertyKey,
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    isSubmitting,
    includeDescription,
    customProps,
}: FieldProps<EntityReference[], TagPickerStrings>) {
    const strings = customProps;
    const locked = disabled || isSubmitting;

    const ofProperty = property.of as ResolvedProperty;
    // `path` is optional on a reference property — FireCMS allows a dynamic one
    // resolved later — but this field is only ever used on `news.tags`, and a
    // picker with no collection to pick from has nothing to render.
    if (ofProperty?.dataType !== "reference" || !ofProperty.path) {
        throw Error("TagChipsField expects an array of references with a path");
    }
    const path = ofProperty.path;

    const navigationController = useNavigationController();
    const collection = navigationController.getCollection(path);
    if (!collection) {
        throw Error(`TagChipsField: no collection registered at path ${path}`);
    }

    const references = value ?? [];

    /**
     * FireCMS's own multiselect picker, opened by the button below.
     *
     * The same hook `ArrayOfReferencesFieldBinding` uses, and the reason this
     * field is a thin component rather than a reimplementation: search,
     * pagination and the `forceFilter` all stay FireCMS's. Its rows carry no
     * entity actions — a picker row is a checkbox — so opening it does not
     * reintroduce a path to editing a tag.
     */
    const picker = useReferenceDialog({
        multiselect: true,
        path,
        collection,
        selectedEntityIds: references.map((reference) => reference.id),
        forceFilter: ofProperty.forceFilter,
        onMultipleEntitiesSelected: (entities: Entity<Tag>[]) =>
            setValue(entities.map((entity) => getReferenceFrom(entity))),
    });

    const removeAt = (index: number) =>
        setValue(references.filter((_, i) => i !== index));

    return (
        <>
            <div className={cls("rounded-md p-4", fieldBackgroundMixin)}>
                <LabelWithIconAndTooltip
                    propertyKey={propertyKey}
                    icon={getIconForProperty(property, "small")}
                    required={property.validation?.required}
                    title={property.name}
                    className="text-text-secondary dark:text-text-secondary-dark"
                />

                <div className="flex flex-wrap items-center gap-2 mt-2">
                    {references.length === 0 && (
                        <Typography variant="body2" color="secondary">
                            {strings.empty}
                        </Typography>
                    )}

                    {references.map((reference, index) => (
                        <TagChip
                            // Two chips for the same tag cannot exist — the
                            // picker is a set — so the id is a stable key, and
                            // a better one than the index: removing a chip
                            // shifts every index after it.
                            key={reference.id}
                            reference={reference}
                            collection={collection}
                            disabled={Boolean(locked)}
                            removeLabel={strings.remove}
                            onRemove={() => removeAt(index)}
                        />
                    ))}

                    <Button
                        variant="text"
                        size="small"
                        disabled={locked}
                        onClick={() => picker.open()}
                    >
                        <EditIcon size="small" />
                        {strings.edit}
                    </Button>
                </div>
            </div>

            <FieldHelperText
                includeDescription={includeDescription}
                showError={showError}
                error={error}
                disabled={disabled}
                property={property}
            />
        </>
    );
}

/**
 * One tag, named in the editor's own language.
 *
 * A component per chip because the name has to be fetched: an article stores
 * references, and only the tag record knows its labels. `useEntityFetch` with
 * `useCache` is what `ReferencePreview` uses for the same job, so a tag shared
 * by several open forms is fetched once and stays live if its label changes.
 *
 * While the fetch is in flight the chip shows the tag's slug rather than a
 * skeleton: it is the id the reference already carries, it is recognisable, and
 * it makes the row settle at its final size immediately.
 */
function TagChip({
    reference,
    collection,
    disabled,
    removeLabel,
    onRemove,
}: {
    reference: EntityReference;
    collection: EntityCollection;
    disabled: boolean;
    removeLabel: (name: string) => string;
    onRemove: () => void;
}) {
    /**
     * `useTranslation` rather than `currentCmsLocale()`, which reads
     * localStorage: this is a component, and the switcher has to re-render it.
     */
    const { i18n } = useTranslation();
    const { entity } = useEntityFetch<Partial<Tag>, User>({
        path: reference.path,
        entityId: reference.id,
        collection: collection as EntityCollection<Partial<Tag>>,
        useCache: true,
    });

    const name = entity
        ? tagDisplayName(entity, i18n.language as CmsLocale)
        : reference.id;

    return (
        <Chip
            size="small"
            icon={
                disabled ? undefined : (
                    <button
                        type="button"
                        // `type="button"` is load-bearing: the entity form is a
                        // real <form>, and a default-type button inside it
                        // submits — so removing a tag would save the article.
                        title={removeLabel(name)}
                        aria-label={removeLabel(name)}
                        className="flex items-center opacity-60 hover:opacity-100"
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove();
                        }}
                    >
                        <CloseIcon size="smallest" />
                    </button>
                )
            }
        >
            {name}
        </Chip>
    );
}
